"use client";

import React, { useEffect } from "react";
import { FaqSection } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";
import VideoContent from "@/components/VideoContent";

interface FaqSectionProps {
  blok?: FaqSection & SbBlokData;
}

const FaqSectionComponent: React.FC<FaqSectionProps> = ({ blok }) => {
  useEffect(() => {
    if (typeof window !== "undefined") {
      import("bootstrap").then((bootstrap) => {
        document
          .querySelectorAll(".accordion-collapse")
          .forEach((collapseElement) => {
            new bootstrap.Collapse(collapseElement, {
              toggle: false,
            });
          });
      });
    }
  }, []);

  if (!blok) return null;

  const { title, faqs } = blok;

  const stripHtml = (html: string) => {
    return html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .trim();
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs?.map((faq) => ({
      "@type": "Question",
      name: faq.title,
      acceptedAnswer: {
        "@type": "Answer",
        text: stripHtml(renderRichText(faq.description)),
      },
    })),
  };

  const renderContent = (description: any) => {
    if (!description) return null;

    const hasVideoContent = description?.content?.some(
      (item: any) =>
        item.type === "blok" &&
        item.attrs?.body?.some(
          (block: any) => block.component === "video_content"
        )
    );

    if (!hasVideoContent) {
      return (
        <div
          dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
        />
      );
    }

    return (
      <>
        {description.content.map((item: any, index: number) => {
          if (item.type === "blok" && item.attrs?.body) {
            return item.attrs.body.map((block: any) => {
              if (block.component === "video_content") {
                return <VideoContent key={block._uid} blok={block} />;
              }
              return null;
            });
          }
          if (item.type === "paragraph") {
            return (
              <div
                key={index}
                dangerouslySetInnerHTML={{
                  __html: renderRichText({ type: "doc", content: [item] }),
                }}
              />
            );
          }
          return null;
        })}
      </>
    );
  };

  const midPoint = Math.ceil((faqs?.length || 0) / 2);
  const leftColumnFaqs = faqs?.slice(0, midPoint) || [];
  const rightColumnFaqs = faqs?.slice(midPoint) || [];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <section
        className="container py-4 my-2 py-md-5 my-md-3"
        {...storyblokEditable(blok)}
      >
        {title && <h2 className="text-center mb-5 pb-3">{title}</h2>}
        <div
          className="faq-columns row"
          id="js-faqs"
          itemScope
          itemType="https://schema.org/FAQPage"
        >
          <div className="col-lg-12">
            {leftColumnFaqs.map((faq, index) => (
              <div
                key={faq._uid}
                className="faq-column card card-lighter p-2 px-3 mb-3"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <h3 className="h4" itemProp="name">
                  <button
                    className="accordion-button"
                    type="button"
                    id={`heading-left-${index}`}
                    data-bs-toggle="collapse"
                    data-bs-target={`#faqleft-${index}`}
                    aria-expanded="false"
                    aria-controls={`faqleft-${index}`}
                  >
                    {faq.title}
                  </button>
                </h3>
                <div
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  id={`faqleft-${index}`}
                  className="accordion-collapse collapse"
                  aria-labelledby={`heading-left-${index}`}
                  data-bs-parent="#js-faqs"
                >
                  <div className="my-1" itemProp="text">
                    {renderContent(faq.description)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="col-lg-12">
            {rightColumnFaqs.map((faq, index) => (
              <div
                key={faq._uid}
                className="faq-column card card-lighter p-2 px-3 mb-3"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <h3 className="h4" itemProp="name">
                  <button
                    className="accordion-button"
                    type="button"
                    id={`heading-right-${index}`}
                    data-bs-toggle="collapse"
                    data-bs-target={`#faqright-${index}`}
                    aria-expanded="false"
                    aria-controls={`faqright-${index}`}
                  >
                    {faq.title}
                  </button>
                </h3>
                <div
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  id={`faqright-${index}`}
                  className="accordion-collapse collapse"
                  aria-labelledby={`heading-right-${index}`}
                  data-bs-parent="#js-faqs"
                >
                  <div className="my-1" itemProp="text">
                    {renderContent(faq.description)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default FaqSectionComponent;